import { supabase } from './supabase';

export interface CustomerVehicle {
  id: string;
  customer_id: string;
  make: string;
  model: string;
  year: number;
  /** Trim level, usually filled by the VIN decoder (optional for callers that predate it). */
  trim?: string | null;
  vin: string | null;
  color: string | null;
  /** Factory paint code, usually filled by the VIN decoder (optional for callers that predate it). */
  paint_code?: string | null;
  license_plate: string | null;
  mileage: number | null;
  notes: string | null;
  is_primary: boolean;
  last_service: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerTag {
  id: string;
  business_id: string;
  name: string;
  color: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** One extra address row stored in `customers.additional_addresses` (jsonb). */
export interface CustomerAdditionalAddress {
  label?: string | null;
  address: string;
  lat?: number | null;
  lng?: number | null;
}

export function parseCustomerAdditionalAddresses(raw: unknown): CustomerAdditionalAddress[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(normalizeCustomerAdditionalAddressRow).filter((r) => r.address.length > 0);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parseCustomerAdditionalAddresses(parsed);
    } catch {
      return [];
    }
  }
  return [];
}

/** Same shape as DB rows but keeps empty `address` rows so form "add row" state is not stripped on re-render. */
export function parseCustomerAdditionalAddressesForForm(raw: unknown): CustomerAdditionalAddress[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(normalizeCustomerAdditionalAddressRow);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parseCustomerAdditionalAddressesForForm(parsed);
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeCustomerAdditionalAddressRow(r: unknown): CustomerAdditionalAddress {
  const o = r && typeof r === 'object' ? (r as Record<string, unknown>) : {};
  const label = o.label;
  return {
    label: typeof label === 'string' && label.trim() ? label.trim() : null,
    address: String(o.address ?? '').trim(),
    lat: o.lat != null && o.lat !== '' ? Number(o.lat) : null,
    lng: o.lng != null && o.lng !== '' ? Number(o.lng) : null,
  };
}

/** Strip empty rows; returns [] when none (persist as jsonb []). */
export function sanitizeCustomerAdditionalAddressesForSave(
  entries: CustomerAdditionalAddress[],
): CustomerAdditionalAddress[] {
  return entries
    .map((e) => ({
      label: (e.label || '').trim() || null,
      address: (e.address || '').trim(),
      lat: e.lat != null && !Number.isNaN(Number(e.lat)) ? Number(e.lat) : null,
      lng: e.lng != null && !Number.isNaN(Number(e.lng)) ? Number(e.lng) : null,
    }))
    .filter((e) => e.address.length > 0);
}

export function emptyCustomerAdditionalAddress(): CustomerAdditionalAddress {
  return { label: null, address: '', lat: null, lng: null };
}

export interface Customer {
  id: string;
  business_id: string;
  type: 'person' | 'business';
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /** Extra addresses (optional labels). Primary stays in `address` / `lat` / `lng`. */
  additional_addresses?: CustomerAdditionalAddress[] | null;
  source: string | null;
  tags: string[];
  Tags?: CustomerTag[] | null; // JSONB field for assigned tags
  last_interaction: string | null;
  lifetime_value: number;
  next_service_date: string | null;
  referral_source: string | null;
  preferences: Record<string, any>;
  status: string;
  amount_spent: number;
  credit_balance: number;
  wallet: number;
  notes: string | null;
  tax_exempt: boolean;
  workflow_exempt: boolean;
  carfax_exempt: boolean;
  /** Business/company name (B2B) — printed on invoices as bill_to_company. */
  company?: string | null;
  /** B2B terms: portal-authenticated store checkouts skip pay-at-checkout
   *  and are invoiced on delivery instead. */
  invoice_only?: boolean;
  /** Default invoice terms for this customer's orders ('net_30' etc).
   *  Null = business default from invoice settings. */
  default_payment_terms?: string | null;
  receive_reminders: boolean;
  receive_offers: boolean;
  is_supplier: boolean;
  /** Supplier only: preferred contact method. Null for regular customers. */
  preferred_contact_method: 'email' | 'sms' | 'both' | null;
  repeat: boolean;
  /** Stripe Customer on the business Connect account (for saved cards). */
  stripe_customer_id?: string | null;
  created_at: string;
  updated_at: string;
  vehicles?: CustomerVehicle[];
  total_bookings?: number;
  total_vehicles?: number;
  total_spent?: number;
  last_booking_date?: string;
  recent_bookings?: {
    id: string;
    booking_number: number;
    title: string;
    status: string;
    total_amount: number;
    created_at: string;
  }[];
}

export async function getCustomers(businessId: string) {
  console.log('🔍 getCustomers called for business:', businessId);
  
  // Supabase has a default limit of 1000 rows, so we need to fetch in batches
  const BATCH_SIZE = 1000;
  let allCustomers: Customer[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('customers')
      .select(`
        *,
        vehicles:customer_vehicles(*)
      `)
      .eq('business_id', businessId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .range(offset, offset + BATCH_SIZE - 1);

    if (error) throw error;

    if (data && data.length > 0) {
      allCustomers = [...allCustomers, ...(data as Customer[])];
      offset += BATCH_SIZE;
      hasMore = data.length === BATCH_SIZE; // If we got a full batch, there might be more
    } else {
      hasMore = false;
    }
  }

  console.log('📊 getCustomers result:', {
    count: allCustomers.length,
    batches: Math.ceil(offset / BATCH_SIZE),
    sampleCustomers: allCustomers.slice(0, 3).map(c => ({ name: `${c.first_name} ${c.last_name}`, email: c.email, vehicleCount: c.vehicles?.length || 0 })),
    hasJoshS: allCustomers.some(c => c.email === 'josh@swyf.ca'),
    joshSCustomer: allCustomers.find(c => c.email === 'josh@swyf.ca')
  });

  return allCustomers;
}

/**
 * Server-side paginated + searched customers for the Customers page.
 * Returns one page (default 30) plus the total matching count so the UI can
 * render "Page X of Y" controls — instead of downloading every customer.
 */
export async function getCustomersPaged(
  businessId: string,
  opts: {
    page?: number;
    pageSize?: number;
    search?: string;
    /** Only these ids (Active Customer view). Empty array = no results. */
    idIn?: string[];
    /** Exclude these ids (Inactive view = everyone without a recent invoice). */
    idNotIn?: string[];
  } = {},
): Promise<{ rows: Customer[]; total: number }> {
  const page = Math.max(opts.page ?? 1, 1);
  const pageSize = Math.max(opts.pageSize ?? 30, 1);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  if (opts.idIn && opts.idIn.length === 0) return { rows: [], total: 0 };

  let q = supabase
    .from('customers')
    .select(`*, vehicles:customer_vehicles(*)`, { count: 'exact' })
    .eq('business_id', businessId)
    .eq('status', 'active');

  if (opts.idIn) q = q.in('id', opts.idIn);
  if (opts.idNotIn && opts.idNotIn.length > 0) {
    q = q.not('id', 'in', `(${opts.idNotIn.join(',')})`);
  }

  const search = (opts.search || '').trim();
  if (search) {
    // Strip characters that would break the PostgREST or() filter syntax.
    const term = search.replace(/[%,()]/g, ' ').trim();
    if (term) {
      const like = `%${term}%`;
      const orParts = [
        `first_name.ilike.${like}`,
        `last_name.ilike.${like}`,
        `company.ilike.${like}`,
        `email.ilike.${like}`,
        `phone.ilike.${like}`,
      ];
      // Full-name searches ("josh s") span two columns — no single column
      // contains the whole term, so also match first + last name together.
      const tokens = term.split(/\s+/).filter(Boolean);
      if (tokens.length >= 2) {
        orParts.push(
          `and(first_name.ilike.%${tokens[0]}%,last_name.ilike.%${tokens.slice(1).join(' ')}%)`,
        );
      }
      q = q.or(orParts.join(','));
    }
  }

  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw error;
  return { rows: (data || []) as Customer[], total: count ?? 0 };
}

export async function getCustomer(id: string) {
  if (!id || typeof id !== 'string') {
    throw new Error('Customer ID is required');
  }

  // Use customers table directly to ensure we get the repeat field
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      vehicles:customer_vehicles(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching customer:', error);
    throw new Error('Unable to load customer details');
  }

  if (!data) {
    throw new Error('Customer not found');
  }

  // Ensure repeat field defaults to false if undefined
  const customer = data as Customer;
  if (customer.repeat === undefined) {
    customer.repeat = false;
  }

  return customer;
}

/**
 * Checks if a customer with the given email or phone already exists (active customers only)
 * @returns Object with isDuplicate flag and matchingCustomer data if found
 */
function getPhoneVariants(phone: string): string[] {
  const digits = phone.replace(/\D/g, '');
  const variants = new Set<string>();
  variants.add(phone);

  if (digits.length === 11 && digits.startsWith('1')) {
    const local = digits.slice(1);
    variants.add(`+${digits}`);        // +16047798059
    variants.add(digits);              // 16047798059
    variants.add(local);               // 6047798059
    variants.add(`${local.slice(0,3)}-${local.slice(3,6)}-${local.slice(6)}`); // 604-779-8059
  } else if (digits.length === 10) {
    variants.add(`+1${digits}`);       // +16047798059
    variants.add(`1${digits}`);        // 16047798059
    variants.add(digits);              // 6047798059
    variants.add(`${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`); // 604-779-8059
  }

  return Array.from(variants);
}

export async function checkDuplicateCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  const cleanEmail = email?.trim() || null;
  const cleanPhone = phone?.trim() || null;
  
  if (!cleanEmail && !cleanPhone) {
    return { isDuplicate: false, matchingCustomer: null };
  }

  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  const phoneVariants = cleanPhone ? getPhoneVariants(cleanPhone) : [];
  const phoneFilter = phoneVariants.map(v => `phone.eq.${v}`).join(',');

  if (cleanEmail && !cleanPhone) {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .eq('email', cleanEmail);

    if (error) throw new Error('Unable to check for duplicate customers (email)');

    return {
      isDuplicate: data && data.length > 0,
      matchingCustomer: data && data.length > 0 ? data[0] : null
    };
  }

  if (cleanPhone && !cleanEmail) {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .or(phoneFilter);

    if (error) throw new Error('Unable to check for duplicate customers (phone)');

    return {
      isDuplicate: data && data.length > 0,
      matchingCustomer: data && data.length > 0 ? data[0] : null
    };
  }

  if (cleanEmail && cleanPhone) {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'active')
      .or(`email.eq.${cleanEmail},${phoneFilter}`);

    if (error) throw new Error('Unable to check for duplicate customers (both)');

    return {
      isDuplicate: data && data.length > 0,
      matchingCustomer: data && data.length > 0 ? data[0] : null
    };
  }

  return { isDuplicate: false, matchingCustomer: null };
}

/**
 * Checks if a customer with the given email or phone exists in deleted status
 * @returns Object with isArchived flag and archivedCustomer data if found
 */
export async function checkArchivedCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  if (!email && !phone) {
    return { isArchived: false, archivedCustomer: null };
  }

  const cleanPhone = phone?.trim() || null;
  const phoneVariants = cleanPhone ? getPhoneVariants(cleanPhone) : [];
  const phoneFilter = phoneVariants.map(v => `phone.eq.${v}`).join(',');

  const query = supabase!
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'deleted');

  if (email && cleanPhone) {
    query.or(`email.eq.${email},${phoneFilter}`);
  } else if (email) {
    query.eq('email', email);
  } else if (cleanPhone) {
    query.or(phoneFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error checking for deleted customer:', error);
    throw new Error('Unable to check for deleted customers');
  }

  return {
    isArchived: data && data.length > 0,
    archivedCustomer: data && data.length > 0 ? data[0] : null
  };
}

/** Columns Postgres computes itself (GENERATED ALWAYS). Never include them in an insert or update. */
const GENERATED_CUSTOMER_COLUMNS = ['phone_key', 'email_key'] as const;

function stripGeneratedCustomerColumns<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = { ...row };
  for (const key of GENERATED_CUSTOMER_COLUMNS) delete out[key];
  return out as T;
}

export async function createCustomer(
  customer: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'vehicles'>,
  vehicles?: Omit<CustomerVehicle, 'id' | 'customer_id' | 'created_at' | 'updated_at'>[]
) {

  // Check for active customer duplicates first
  const { isDuplicate, matchingCustomer } = await checkDuplicateCustomer(
    customer.business_id,
    customer.email,
    customer.phone
  );

  if (isDuplicate) {
    let duplicateFields = [];
    if (matchingCustomer?.email === customer.email && customer.email) {
      duplicateFields.push('email');
    }
    if (matchingCustomer?.phone === customer.phone && customer.phone) {
      duplicateFields.push('phone number');
    }
    
    throw new Error(
      `A customer with this ${duplicateFields.join(' and ')} already exists. `+
      `Customer: ${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`
    );
  }

  // Check for archived customer conflicts
  const { isArchived, archivedCustomer } = await checkArchivedCustomer(
    customer.business_id,
    customer.email,
    customer.phone
  );

  if (isArchived) {
    let archivedFields = [];
    if (archivedCustomer?.email === customer.email && customer.email) {
      archivedFields.push('email');
    }
    if (archivedCustomer?.phone === customer.phone && customer.phone) {
      archivedFields.push('phone number');
    }
    
    throw new Error(
      `This ${archivedFields.join(' and ')} belongs to an archived customer: ${archivedCustomer?.first_name} ${archivedCustomer?.last_name}. Please contact support to reactivate the customer or use different contact information.`
    );
  }

  // Create customer
  const { data: customerData, error: customerError } = await supabase
    .from('customers')
    .insert([stripGeneratedCustomerColumns({
      ...customer,
      is_supplier: customer.is_supplier ?? false // Default to false if not specified
    })])
    .select()
    .single();

  if (customerError) throw customerError;

  // Create vehicles if provided
  if (vehicles && vehicles.length > 0) {
    const { error: vehiclesError } = await supabase
      .from('customer_vehicles')
      .insert(
        vehicles.map(vehicle =>
          sanitizeCustomerVehicleForInsert({
            ...vehicle,
            customer_id: customerData.id,
          })
        )
      );

    if (vehiclesError) throw vehiclesError;
  }

  // Auto-create map pin if customer has coordinates
  if (customerData.lat && customerData.lng && customerData.address) {
    try {
      console.log('🎯 Auto-creating map pin for new customer:', {
        name: `${customerData.first_name} ${customerData.last_name}`,
        address: customerData.address,
        coordinates: { lat: customerData.lat, lng: customerData.lng }
      });

      const { error: markerError } = await supabase
        .from('address_markers')
        .insert({
          business_id: customerData.business_id,
          customer_id: customerData.id,
          latitude: customerData.lat,
          longitude: customerData.lng,
          marker_type: 'customer',
          address: customerData.address,
          notes: `Customer: ${customerData.type === 'business' ? customerData.first_name : `${customerData.first_name} ${customerData.last_name}`.trim()}`,
          created_by: null
        });

      if (markerError) {
        console.error('❌ Failed to auto-create map pin:', markerError);
        // Don't throw error - customer creation should succeed even if pin creation fails
      } else {
        console.log('✅ Auto-created map pin for new customer');
      }
    } catch (error) {
      console.error('❌ Exception during auto map pin creation:', error);
      // Don't throw error - customer creation should succeed even if pin creation fails
    }
  }

  return getCustomer(customerData.id);
}

export async function updateCustomer(
  id: string, 
  updates: Partial<Customer> & { 
    vehicles?: (Omit<CustomerVehicle, 'id' | 'customer_id' | 'created_at' | 'updated_at'> & { id?: string })[]
  }
) {
  if (!id || typeof id !== 'string') {
    throw new Error('Invalid customer ID provided');
  }

  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  const { vehicles, ...customerUpdates } = updates;

  // Validate required fields only when they are being updated (partial updates like preferred_contact_method skip this)
  if ('first_name' in customerUpdates && !customerUpdates.first_name?.trim()) {
    throw new Error('Please enter a first name');
  }
  if ('last_name' in customerUpdates && !customerUpdates.last_name?.trim()) {
    throw new Error('Please enter a last name');
  }
  if ('phone' in customerUpdates && !customerUpdates.phone?.trim()) {
    throw new Error('Please enter a phone number');
  }

  // Get current customer record
  const { data: currentCustomer, error: fetchError } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('Error fetching customer:', fetchError);
    throw new Error('Unable to load customer details. Please try again.');
  }

  if (!currentCustomer) {
    throw new Error('Customer not found. They may have been deleted or moved.');
  }

  // Create a clean updates object, removing view-specific fields that don't exist in the customers table
  // Type assertion to help TypeScript understand the structure
  const customerUpdatesTyped = customerUpdates as Partial<Customer & {
    last_booking_date?: string;
    total_bookings?: number;
    total_vehicles?: number;
    total_spent?: number;
    recent_bookings?: any[];
    vehicles?: any[];
  }>;

  // Now destructure with proper typing
  const {
    last_booking_date,
    total_bookings,
    total_vehicles,
    total_spent,
    recent_bookings,
    vehicles: _ignoredVehicles, // Ignore this field
    ...updateData
  } = customerUpdatesTyped;

  // Only send the changed fields. Echoing the whole fetched row back fails:
  // phone_key / email_key are GENERATED ALWAYS columns and Postgres rejects
  // any explicit value for them ("can only be updated to DEFAULT").
  const payload: Record<string, unknown> = { ...updateData };
  for (const key of GENERATED_CUSTOMER_COLUMNS) delete payload[key];
  delete payload.id;
  payload.business_id = currentCustomer.business_id;
  payload.updated_at = new Date().toISOString();

  // Update customer
  const { data, error } = await supabase
    .from('customers')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating customer:', error);
    throw new Error(`Unable to update customer details. ${error.message || 'Please try again.'}`);
  }

  // Handle vehicles separately if present
  if (vehicles && vehicles.length > 0) {
    // Get existing vehicles
    const { data: existingVehicles, error: fetchError } = await supabase
      .from('customer_vehicles')
      .select('id')
      .eq('customer_id', id);

    if (fetchError) {
      console.error('Error fetching vehicles:', fetchError);
      throw new Error('Unable to load vehicle details. Please try again.');
    }

    const existingIds = new Set(existingVehicles.map(v => v.id));
    const updatedIds = new Set(vehicles.filter(v => v.id).map(v => v.id));

    // Delete vehicles that are no longer in the list
    const vehiclesToDelete = Array.from(existingIds).filter(vehicleId => !updatedIds.has(vehicleId));
    if (vehiclesToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('customer_vehicles')
        .delete()
        .in('id', vehiclesToDelete);

      if (deleteError) {
        console.error('Error deleting vehicles:', deleteError);
        throw new Error('Unable to remove vehicles. Please try again.');
      }
    }

    // Update existing vehicles
    const vehiclesToUpdate = vehicles.filter(v => v.id && existingIds.has(v.id));
    for (const vehicle of vehiclesToUpdate) {
      const { id: vehicleId, ...vehicleUpdateData } = vehicle;
      
      if (!vehicleId) continue; // TypeScript safety check

      const payload = sanitizeCustomerVehicleForUpdate(vehicleUpdateData);
      if (Object.keys(payload).length === 0) continue;
      
      const { error: updateError } = await supabase
        .from('customer_vehicles')
        .update(payload)
        .eq('id', vehicleId);

      if (updateError) {
        console.error('Error updating vehicle:', updateError);
        throw new Error('Unable to update vehicle details. Please try again.');
      }
    }

    // Insert new vehicles
    const vehiclesToInsert = vehicles.filter(v => !v.id);
    if (vehiclesToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('customer_vehicles')
        .insert(
          vehiclesToInsert.map(vehicle =>
            sanitizeCustomerVehicleForInsert({
              ...vehicle,
              customer_id: id,
            })
          )
        );

      if (insertError) {
        console.error('Error adding vehicles:', insertError);
        throw new Error('Unable to add new vehicles. Please try again.');
      }
    }
  }

  // Auto-update map pin if address or coordinates changed
  const addressChanged = currentCustomer.address !== data.address;
  const coordinatesChanged = currentCustomer.lat !== data.lat || currentCustomer.lng !== data.lng;
  
  if ((addressChanged || coordinatesChanged) && data.lat && data.lng && data.address) {
    try {
      console.log('🎯 Auto-updating map pin for customer:', {
        name: `${data.first_name} ${data.last_name}`,
        addressChanged,
        coordinatesChanged,
        newAddress: data.address,
        newCoordinates: { lat: data.lat, lng: data.lng }
      });

      // Check if customer already has a map marker
      const { data: existingMarker, error: fetchMarkerError } = await supabase
        .from('address_markers')
        .select('id')
        .eq('customer_id', id)
        .eq('marker_type', 'customer')
        .single();

      if (fetchMarkerError && fetchMarkerError.code !== 'PGRST116') {
        console.error('❌ Error checking existing marker:', fetchMarkerError);
      } else if (existingMarker) {
        // Update existing marker
        const { error: updateMarkerError } = await supabase
          .from('address_markers')
          .update({
            latitude: data.lat,
            longitude: data.lng,
            address: data.address,
            notes: `Customer: ${data.type === 'business' ? data.first_name : `${data.first_name} ${data.last_name}`.trim()}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingMarker.id);

        if (updateMarkerError) {
          console.error('❌ Failed to update map pin:', updateMarkerError);
        } else {
          console.log('✅ Updated existing map pin for customer');
        }
      } else {
        // Create new marker
        const { error: createMarkerError } = await supabase
          .from('address_markers')
          .insert({
            business_id: data.business_id,
            customer_id: data.id,
            latitude: data.lat,
            longitude: data.lng,
            marker_type: 'customer',
            address: data.address,
            notes: `Customer: ${data.type === 'business' ? data.first_name : `${data.first_name} ${data.last_name}`.trim()}`,
            created_by: null
          });

        if (createMarkerError) {
          console.error('❌ Failed to create map pin:', createMarkerError);
        } else {
          console.log('✅ Created new map pin for customer');
        }
      }
    } catch (error) {
      console.error('❌ Exception during auto map pin update:', error);
      // Don't throw error - customer update should succeed even if pin update fails
    }
  } else if (!data.address || (!data.lat || !data.lng)) {
    // Remove map pin if customer no longer has address or coordinates
    try {
      const { error: deleteMarkerError } = await supabase
        .from('address_markers')
        .delete()
        .eq('customer_id', id)
        .eq('marker_type', 'customer');

      if (deleteMarkerError) {
        console.error('❌ Failed to remove map pin:', deleteMarkerError);
      } else {
        console.log('🗑️ Removed map pin for customer (no address/coordinates)');
      }
    } catch (error) {
      console.error('❌ Exception during map pin removal:', error);
    }
  }

  // Return the updated customer with all data
  return await getCustomer(id);
}

async function deleteCustomer(id: string) {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export { deleteCustomer };

function emptyStringToNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function normalizeVehicleMileage(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value);
}

/** Strip read-only / nested fields so PostgREST only receives valid table columns. */
export function sanitizeCustomerVehicleForInsert(
  vehicle: Omit<CustomerVehicle, 'id' | 'created_at' | 'updated_at'>
) {
  return {
    customer_id: vehicle.customer_id,
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    trim: emptyStringToNull(vehicle.trim),
    vin: emptyStringToNull(vehicle.vin),
    color: emptyStringToNull(vehicle.color),
    paint_code: emptyStringToNull(vehicle.paint_code),
    license_plate: emptyStringToNull(vehicle.license_plate),
    mileage: normalizeVehicleMileage(vehicle.mileage),
    notes: emptyStringToNull(vehicle.notes),
    is_primary: vehicle.is_primary ?? false,
    last_service: vehicle.last_service ?? null,
  };
}

export function sanitizeCustomerVehicleForUpdate(
  updates: Partial<CustomerVehicle>
) {
  const payload: Record<string, unknown> = {};

  if (updates.make !== undefined) payload.make = updates.make;
  if (updates.model !== undefined) payload.model = updates.model;
  if (updates.year !== undefined) payload.year = updates.year;
  if ('trim' in updates) payload.trim = emptyStringToNull(updates.trim);
  if ('vin' in updates) payload.vin = emptyStringToNull(updates.vin);
  if ('color' in updates) payload.color = emptyStringToNull(updates.color);
  if ('paint_code' in updates) payload.paint_code = emptyStringToNull(updates.paint_code);
  if ('license_plate' in updates) payload.license_plate = emptyStringToNull(updates.license_plate);
  if ('mileage' in updates) payload.mileage = normalizeVehicleMileage(updates.mileage);
  if ('notes' in updates) payload.notes = emptyStringToNull(updates.notes);
  if ('is_primary' in updates) payload.is_primary = updates.is_primary;
  if ('last_service' in updates) payload.last_service = updates.last_service ?? null;

  return payload;
}

async function getCustomerVehicles(customerId: string) {
  const { data, error } = await supabase
    .from('customer_vehicles')
    .select('*')
    .eq('customer_id', customerId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as CustomerVehicle[];
}

export async function getCustomerVehicle(id: string) {
  if (!id || typeof id !== 'string') {
    throw new Error('Vehicle ID is required');
  }

  const { data, error } = await supabase
    .from('customer_vehicles')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as CustomerVehicle;
}

export async function createCustomerVehicle(
  vehicle: Omit<CustomerVehicle, 'id' | 'created_at' | 'updated_at'>
) {
  const payload = sanitizeCustomerVehicleForInsert(vehicle);
  const { data, error } = await supabase
    .from('customer_vehicles')
    .insert([payload])
    .select()
    .single();

  if (error) throw error;
  return data as CustomerVehicle;
}

export async function updateCustomerVehicle(
  id: string,
  updates: Partial<CustomerVehicle>
) {
  const payload = sanitizeCustomerVehicleForUpdate(updates);
  if (Object.keys(payload).length === 0) {
    return getCustomerVehicle(id);
  }

  const { data, error } = await supabase
    .from('customer_vehicles')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CustomerVehicle;
}

export async function deleteCustomerVehicle(id: string) {
  const { error } = await supabase
    .from('customer_vehicles')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

async function getCustomerStats(businessId: string) {
  const { data: customers, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalCustomers: customers.length,
    activeCustomers: customers.filter(c => c.status === 'active').length,
    totalSpent: customers.reduce((sum, c) => sum + (c.amount_spent || 0), 0),
    averageSpent: customers.length > 0 
      ? customers.reduce((sum, c) => sum + (c.amount_spent || 0), 0) / customers.length 
      : 0
  };

  return stats;
}

/**
 * Finds an existing customer by phone number or email
 * @returns The matching customer if found, or null if no match
 */
export async function findExistingCustomer(
  businessId: string,
  email?: string | null,
  phone?: string | null
) {
  if (!email && !phone) {
    return null;
  }

  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  // Try to find by phone first (phone is usually more unique)
  if (phone) {
    const variants = getPhoneVariants(phone);
    const phoneFilter = variants.map(v => `phone.eq.${v}`).join(',');
    const { data: phoneMatches, error: phoneError } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .or(phoneFilter);
    
    if (phoneError) {
      console.error('Error checking for customer by phone:', phoneError);
      throw new Error('Unable to check for existing customers');
    }
    
    if (phoneMatches && phoneMatches.length > 0) {
      return phoneMatches[0];
    }
  }
  
  // If no match by phone and we have an email, try email
  if (email) {
    const { data: emailMatches, error: emailError } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', businessId)
      .eq('email', email);
    
    if (emailError) {
      console.error('Error checking for customer by email:', emailError);
      throw new Error('Unable to check for existing customers');
    }
    
    if (emailMatches && emailMatches.length > 0) {
      console.log(`Found customer with matching email: ${email}`);
      return emailMatches[0]; // Return the first matching customer
    }
  }
  
  // No matches found
  return null;
}

/**
 * Get all suppliers for a business (customers where is_supplier = true and not deleted)
 */
export async function getSuppliers(businessId: string) {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_supplier', true)
    .neq('status', 'deleted')
    .order('first_name');

  if (error) throw error;
  return data as Customer[];
}

/**
 * Create a new supplier (customer with is_supplier = true)
 */
export async function createSupplier(
  supplier: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'vehicles' | 'is_supplier'>
) {
  return createCustomer({
    ...supplier,
    is_supplier: true
  });
}

/**
 * Updates an existing customer with information from an online booking
 * @returns The updated customer record
 */
export async function updateCustomerFromBooking(
  customerId: string,
  updates: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    address?: string;
  }
) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Database connection is not available');
  }

  // Get the existing customer
  const { data: existingCustomer, error: fetchError } = await supabase
    .from('customers')
    .select('*')
    .eq('id', customerId)
    .single();

  if (fetchError) {
    console.error('Error fetching customer to update:', fetchError);
    throw new Error('Unable to load customer details for update');
  }

  if (!existingCustomer) {
    throw new Error('Customer not found for update');
  }

  // Only update fields that are provided and different
  const updateData: Record<string, any> = {};
  
  if (updates.first_name && updates.first_name !== existingCustomer.first_name) {
    updateData.first_name = updates.first_name;
  }
  
  if (updates.last_name && updates.last_name !== existingCustomer.last_name) {
    updateData.last_name = updates.last_name;
  }
  
  if (updates.email && updates.email !== existingCustomer.email) {
    updateData.email = updates.email;
  }
  
  if (updates.phone && updates.phone !== existingCustomer.phone) {
    updateData.phone = updates.phone;
  }
  
  if (updates.address && updates.address !== existingCustomer.address) {
    updateData.address = updates.address;
  }
  
  // If there are no changes, return the existing customer
  if (Object.keys(updateData).length === 0) {
    return existingCustomer;
  }
  
  // Update the customer with the new information
  const { data: updatedCustomer, error: updateError } = await supabase
    .from('customers')
    .update({ 
      ...updateData,
      updated_at: new Date().toISOString()
    })
    .eq('id', customerId)
    .select()
    .single();

  if (updateError) {
    console.error('Error updating customer:', updateError);
    throw new Error('Unable to update customer information');
  }

  return updatedCustomer;
}

// Customer Tags Management Functions

export async function getCustomerTags(businessId: string): Promise<CustomerTag[]> {
  const { data, error } = await supabase
    .from('customer_tags')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return data as CustomerTag[];
}

export async function createCustomerTag(
  businessId: string,
  name: string,
  color?: string | null
): Promise<CustomerTag> {
  const { data, error } = await supabase
    .from('customer_tags')
    .insert([{
      business_id: businessId,
      name: name.trim(),
      color: color || null,
      is_active: true
    }])
    .select()
    .single();

  if (error) throw error;
  return data as CustomerTag;
}

export async function deleteCustomerTag(tagId: string): Promise<void> {
  const { error } = await supabase
    .from('customer_tags')
    .update({ is_active: false })
    .eq('id', tagId);

  if (error) throw error;
}

export async function updateCustomerTags(
  customerId: string,
  tagIds: string[]
): Promise<Customer> {
  if (tagIds.length === 0) {
    // If no tags, set to empty array
    const { data, error } = await supabase
      .from('customers')
      .update({ Tags: [] })
      .eq('id', customerId)
      .select()
      .single();

    if (error) throw error;
    return await getCustomer(customerId);
  }

  // Get the tag details for the tag IDs
  const { data: tags, error: tagsError } = await supabase
    .from('customer_tags')
    .select('id, name, color')
    .in('id', tagIds)
    .eq('is_active', true);

  if (tagsError) throw tagsError;

  // Format tags as JSONB array with full tag objects
  const tagsArray = tags.map(tag => ({
    id: tag.id,
    name: tag.name,
    color: tag.color || '#3B82F6'
  }));

  // Update customer with Tags JSONB field
  const { error } = await supabase
    .from('customers')
    .update({ Tags: tagsArray })
    .eq('id', customerId);

  if (error) throw error;
  return await getCustomer(customerId);
}